import React, { useState, useEffect, useCallback } from 'react';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import ProofThumbnails from '../../../shared/components/ProofThumbnails';

// Direct-to-collector (Phase C3): the collector reviews payments made to them this
// round and confirms/rejects each. The collector-facing counterpart to the admin's
// MemberSubmissionReviewModal. Only rendered when it's the member's turn to collect
// in a direct_to_collector group. COPY: provisional.

const CollectorPaymentsToMe = ({ groupId, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    const { data } = await supabaseClient.rpc('get_collector_round_payments', { p_group_id: groupId });
    setRows(data || []);
    setLoading(false);
  }, [groupId]);
  useEffect(() => { fetchRows(); }, [fetchRows]);

  const confirm = async (id) => {
    setBusyId(id); setError(null);
    try {
      const { error: e } = await supabaseClient.rpc('collector_confirm_payment', { p_payment_id: id });
      if (e) throw e;
      await fetchRows(); onChanged?.();   // last confirmation may advance the round server-side
    } catch (e) { setError(e.message || 'Could not confirm the payment.'); }
    finally { setBusyId(null); }
  };
  const reject = async (id) => {
    if (!reason.trim()) { setError('Please give a reason so the member can fix and resubmit.'); return; }
    setBusyId(id); setError(null);
    try {
      const { error: e } = await supabaseClient.rpc('collector_reject_payment', { p_payment_id: id, p_reason: reason.trim() });
      if (e) throw e;
      setRejectingId(null); setReason('');
      await fetchRows(); onChanged?.();
    } catch (e) { setError(e.message || 'Could not reject the payment.'); }
    finally { setBusyId(null); }
  };

  const pending = rows.filter(r => r.status === 'member_submitted');
  const confirmed = rows.filter(r => r.status === 'paid').length;

  return (
    <div className="bg-neutral-100 rounded-lg shadow-sm border border-primary-300 p-5 mb-6">
      <h3 className="text-base font-bold text-neutral-900">Payments to you this round</h3>
      <p className="text-sm text-neutral-600 mb-3">
        Members pay you directly, then submit a screenshot. Confirm each one you’ve received. When all are confirmed, the round advances automatically.
      </p>
      {error && <p className="text-sm text-error-600 mb-3">{error}</p>}

      {loading ? (
        <p className="text-sm text-neutral-400">Loading…</p>
      ) : pending.length === 0 ? (
        <p className="text-sm text-success-700">Nothing to review right now{confirmed > 0 ? ` — ${confirmed} confirmed` : ''}.</p>
      ) : (
        <div className="space-y-3">
          {pending.map(r => (
            <div key={r.payment_id} className="border border-neutral-200 rounded-lg p-3">
              <div className="flex justify-between items-start gap-2 mb-2">
                <div className="min-w-0">
                  <p className="font-medium text-neutral-900 truncate">{r.member_name || 'Member'}</p>
                  <p className="text-sm text-neutral-600">
                    ${Number(r.submitted_amount || 0).toFixed(2)} · <span className="capitalize">{r.submitted_method || '—'}</span>
                  </p>
                  {r.submitted_reference && <p className="text-xs text-neutral-500 break-all">Ref: {r.submitted_reference}</p>}
                </div>
              </div>
              <div className="mb-3"><ProofThumbnails paymentId={r.payment_id} primaryPath={r.proof_path} /></div>

              {rejectingId === r.payment_id ? (
                <div className="space-y-2">
                  <input
                    type="text" value={reason} onChange={e => setReason(e.target.value)} autoFocus
                    placeholder="Reason (e.g. wrong screenshot / didn’t receive)"
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <div className="flex gap-2">
                    <button onClick={() => { setRejectingId(null); setReason(''); setError(null); }} className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg text-sm text-neutral-700">Cancel</button>
                    <button onClick={() => reject(r.payment_id)} disabled={busyId === r.payment_id} className="flex-1 px-3 py-2 bg-error-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                      {busyId === r.payment_id ? 'Rejecting…' : 'Confirm rejection'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => confirm(r.payment_id)} disabled={busyId === r.payment_id} className="flex-1 px-3 py-2 bg-success-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                    {busyId === r.payment_id ? 'Confirming…' : 'Confirm received'}
                  </button>
                  <button onClick={() => { setRejectingId(r.payment_id); setReason(''); setError(null); }} className="px-3 py-2 bg-error-100 text-error-700 rounded-lg text-sm font-medium">
                    Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CollectorPaymentsToMe;
